import MultiFileUploader, { DocumentData } from '@components/tn-document-uploader/MultiFileUploader';
import TnTypography from '@components/tn-typography/TnTypography';
import { createStyles, lighten, makeStyles, Typography } from '@material-ui/core';
import React, { useEffect, useState } from 'react';

import { InfoOutlined } from '@material-ui/icons';

interface Props {
  documentRowData: DocumentData[];
  addFile: (id: string, fileName: string, url: string, fileIndex: number, fileSize: string) => void;
  deleteFile: (id: string, fileIndex: number) => void;
  onUploadError: () => void;
  isEdit?: boolean;
  parseResume?: (resumeUrl: string) => void;
  resumeParseComplete?: boolean;
  resumeParseError?: boolean;
  resumeError?: string;
  resumeTouched?: boolean;
  isConfirmRehire?: boolean;
  candidateName?: string;
}

const useStyles = makeStyles((theme) =>
  createStyles({
    container: {
      padding: '24px',
    },
    errorMessage: {
      width: '100%',
      margin: '3px 14px 0',
      fontSize: '0.75em',
      lineHeight: 1.66,
      color: theme.palette.error.main,
    },
    bodyContainer: {
      display: 'flex',
      flexDirection: 'row',
      flexWrap: 'wrap',
    },
    labelContainer: {
      width: '100%',
      marginBottom: 16,
    },
    otherDocumentsLabel: {
      width: '100%',
      marginBottom: 16,
      marginTop: 24,
    },
    otherDocumentsContainer: {
      width: '100%',
      display: 'flex',
      flexDirection: 'column',
      gridGap: 16,
    },
    infoContainer: {
      width: '100%',
      border: `1px solid ${lighten(theme.palette.primary.main, 0.7)}`,
      padding: 12,
      marginBottom: 16,
      borderRadius: 8,
      backgroundColor: lighten(theme.palette.primary.main, 0.9),
      display: 'flex',
      gap: 6,
    },
  }),
);

export default function ResumeAndUploads({
  isConfirmRehire = false,
  candidateName,
  documentRowData,
  addFile,
  deleteFile,
  onUploadError,
  isEdit = false,
  parseResume,
  resumeParseComplete = false,
  resumeParseError = false,
  resumeError,
  resumeTouched = false,
}: Readonly<Props>) {
  const classes = useStyles();

  const [resumeSection, setResumeSection] = useState<DocumentData | null>(null);
  const [otherDocumentsSection, setOtherDocumentsSection] = useState<DocumentData[]>([]);

  useEffect(() => {
    const resume = documentRowData.find((doc) => doc.doc_category === 'Resume') ?? null;
    const otherDocuments = documentRowData.filter((doc) => doc.doc_category !== 'Resume');
    setResumeSection(resume);
    setOtherDocumentsSection(otherDocuments);
  }, [documentRowData]);

  return (
    <div className={classes.container}>
      <div className={classes.bodyContainer}>
        {isConfirmRehire && (
          <div className={classes.infoContainer}>
            <InfoOutlined color="primary" fontSize="small" />
            <TnTypography variant="caption2" color="darkGrey">
              {isEdit
                ? 'Please review and update all details as needed.'
                : `The information below has been pre-filled from ${candidateName || 'this person'}'s existing contractor record. Please review and update all details as needed.`}{' '}
              <TnTypography bold variant="caption2" display="inline">
                An updated resume is highly recommended.
              </TnTypography>
            </TnTypography>
          </div>
        )}
        <div className={classes.labelContainer}>
          <TnTypography bold variant="body">
            Upload Resume
          </TnTypography>
        </div>
        {resumeSection && (
          <MultiFileUploader
            addFile={addFile}
            deleteFile={deleteFile}
            documentData={resumeSection}
            onUploadError={onUploadError}
            isEdit={isEdit}
            parseResume={parseResume}
            resumeParseComplete={resumeParseComplete}
            resumeParseError={resumeParseError}
          />
        )}
        {resumeTouched && resumeError && <Typography className={classes.errorMessage}>{resumeError}</Typography>}
        <div className={classes.otherDocumentsLabel}>
          <TnTypography bold variant="body">
            Other Documents
          </TnTypography>
        </div>
        <div className={classes.otherDocumentsContainer}>
          {otherDocumentsSection.map((doc) => (
            <MultiFileUploader
              key={doc.id}
              addFile={addFile}
              deleteFile={deleteFile}
              documentData={doc}
              onUploadError={onUploadError}
              isEdit={isEdit}
            />
          ))}
        </div>
      </div>
    </div>
  );
}
